
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import Barcode from 'react-barcode';
import { Card, CardHeader } from '@components/card/card';
import CustomAlert from '@components/Shared/Alert/CustomAlert';

import FieldEditorCard from './FieldEditorCard';
import { fieldStylePX, FIELD_OPTIONS, PX_PER_MM, presetRoll80x40, type FieldKey } from './labelLayout';
import { ALL_BARCODES, splitBarcodes, type LabelRow, type PriceListBarcodeVM } from './usePriceListBarcodePrint';

type Props = {
  vm: PriceListBarcodeVM;
  onPrint: () => void;
};

export default function SettingsColumn({ vm, onPrint }: Props) {
  const [settingsTab, setSettingsTab] = useState<'basic' | 'custom'>('basic');

  // Preview
  const previewScale = 0.75;
  const previewWpx = Math.max(120, vm.layout.labelWmm * PX_PER_MM * previewScale);
  const previewHpx = Math.max(90, vm.layout.labelHmm * PX_PER_MM * previewScale);

  const previewRow: LabelRow | null = useMemo(() => {
    if (vm.labels.length) return vm.labels[0];
    if (vm.selectedItems.length) {
      const item = vm.selectedItems[0];
      const b = splitBarcodes(item?.barcode)[0] ?? '0000000000000';
      return { key: 'prev', barcode: b, item, price: vm.getUnitPriceForPrint(item) };
    }
    if (vm.data.length) {
      const item = vm.data[0];
      const b = splitBarcodes(item?.barcode)[0] ?? '0000000000000';
      return { key: 'prev', barcode: b, item, price: vm.getUnitPriceForPrint(item) };
    }
    return null;
  }, [vm.labels, vm.selectedItems, vm.data, vm.getUnitPriceForPrint]);

  const saveLayout = () => {
    try {
      vm.saveLayout();
      CustomAlert({ action: 'Add', msg: 'تم حفظ التخطيط' });
    } catch {
      CustomAlert({ action: 'Error', msg: 'تعذر حفظ التخطيط' });
    }
  };

  const loadLayout = () => {
    try {
      const loaded = vm.loadLayout();
      if (!loaded) {
        CustomAlert({ action: 'info', msg: 'لا يوجد تخطيط محفوظ' });
        return;
      }
      vm.setLayout(loaded);
      CustomAlert({ action: 'Add', msg: 'تم تحميل التخطيط' });
    } catch {
      CustomAlert({ action: 'Error', msg: 'تعذر تحميل التخطيط' });
    }
  };

  return (
    <div className='col-lg-5 mb-2'>
      <Card className='shadow-sm border-0 h-100'>
        <CardHeader className='bg-theme bg-opacity-10 border-bottom'>
          <div className='d-flex align-items-center justify-content-between'>
            <h6 className='mb-0'>
              <i className='bi bi-gear me-2'></i>
              إعدادات الطباعة
            </h6>
            <div className='badge bg-theme bg-opacity-25 text-theme'>{vm.labels.length} ملصق</div>
          </div>
        </CardHeader>

        <div className='card-body'>
          {/* Alerts */}
          {!vm.selectedPriceListId ? (
            <div className='alert alert-warning mb-3'>
              <div className='d-flex align-items-center'>
                <i className='bi bi-exclamation-triangle me-2'></i>
                <div className='small'>يجب اختيار قائمة سعر أولاً لتتمكن من طباعة الباركودات</div>
              </div>
            </div>
          ) : vm.selectedItems.length > 0 ? (
            <div className='alert alert-info mb-3'>
              <div className='d-flex align-items-center'>
                <i className='bi bi-printer me-2'></i>
                <div className='small'>
                  جاهز للطباعة: {vm.selectedItems.length} صنف، {vm.totalBarcodes} ملصق
                </div>
              </div>
            </div>
          ) : (
            <div className='alert alert-light mb-3'>
              <div className='d-flex align-items-center'>
                <i className='bi bi-info-circle me-2'></i>
                <div className='small'>حدد أصنافاً من القائمة اليسرى لبدء الطباعة</div>
              </div>
            </div>
          )}

          {/* Settings Tabs */}
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <div className='btn-group btn-group-sm' role='group' aria-label='Settings Tabs'>
              <button
                type='button'
                className={`btn ${settingsTab === 'basic' ? 'btn-theme' : 'btn-outline-secondary'}`}
                onClick={() => setSettingsTab('basic')}
              >
                <i className='bi bi-gear me-1'></i>
                أساسية
              </button>

              <button
                type='button'
                className={`btn ${settingsTab === 'custom' ? 'btn-theme' : 'btn-outline-secondary'}`}
                onClick={() => setSettingsTab('custom')}
              >
                <i className='bi bi-bounding-box me-1'></i>
                مخصصة
              </button>
            </div>

            <span className='badge bg-theme bg-opacity-10 text-theme'>
              {settingsTab === 'basic' ? 'Basic' : 'Custom Layout'}
            </span>
          </div>

          {/* BASIC */}
          {settingsTab === 'basic' ? (
            <div className='row g-3'>
              {/* Presets */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-printer me-1'></i>
                  نوع الطابعة
                </label>

                <div className='row g-2'>
                  <div className='col-6'>
                    <Card>
                      <div className='hover-pointer' onClick={() => vm.applyPreset('ROLL')}>
                        <div className={`card-body text-center p-2 ${vm.paperMode === 'ROLL' ? 'bg-theme bg-opacity-10' : ''}`}>
                          <i className={`bi bi-printer-fill ${vm.paperMode === 'ROLL' ? 'text-theme' : 'text-muted'} display-6 mb-2`} />
                          <div className='small fw-semibold'>Roll (80×40)</div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  <div className='col-6'>
                    <Card>
                      <div className='hover-pointer' onClick={() => vm.applyPreset('A4')}>
                        <div className={`card-body text-center p-2 ${vm.paperMode === 'A4' ? 'bg-theme bg-opacity-10' : ''}`}>
                          <i className={`bi bi-file-earmark ${vm.paperMode === 'A4' ? 'text-theme' : 'text-muted'} display-6 mb-2`} />
                          <div className='small fw-semibold'>A4 (63×38)</div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                <div className='text-muted small mt-2'>
                  <i className='bi bi-info-circle me-1'></i>
                  الإحداثيات وتخطيط القالب في تبويب “مخصصة”.
                </div>
              </div>

              {/* Price settings */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-currency-exchange me-1'></i>
                  إعدادات السعر
                </label>

                <div className='row g-2 mb-2'>
                  <div className='col-6'>
                    <Card>
                      <div className='hover-pointer' onClick={() => vm.setPriceMode('EXCL')}>
                        <div className={`card-body text-center p-2 ${vm.priceMode === 'EXCL' ? 'bg-success bg-opacity-10' : ''}`}>
                          <i className={`bi bi-currency-dollar ${vm.priceMode === 'EXCL' ? 'text-success' : 'text-muted'}`} />
                          <div className='small fw-semibold mt-1'>بدون ضريبة</div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  <div className='col-6'>
                    <Card>
                      <div className='hover-pointer' onClick={() => vm.setPriceMode('INCL')}>
                        <div className={`card-body text-center p-2 ${vm.priceMode === 'INCL' ? 'bg-success bg-opacity-10' : ''}`}>
                          <i className={`bi bi-cash ${vm.priceMode === 'INCL' ? 'text-success' : 'text-muted'}`} />
                          <div className='small fw-semibold mt-1'>شامل ضريبة</div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                <div className='row g-2 align-items-center'>
                  <div className='col-6'>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='printAllBarcodes'
                        checked={vm.printAllBarcodes}
                        onChange={e => vm.setPrintAllBarcodes(e.target.checked)}
                      />
                      <label className='form-check-label small' htmlFor='printAllBarcodes'>
                        <i className='bi bi-upc-scan me-1'></i>
                        طباعة كل الباركودات
                      </label>
                    </div>
                  </div>

                  <div className='col-6'>
                    <input
                      className='form-control form-control-sm'
                      value={vm.currency}
                      onChange={e => vm.setCurrency(e.target.value)}
                      placeholder='العملة'
                    />
                  </div>
                </div>
              </div>

              {/* Display toggles */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-eye me-1'></i>
                  خيارات العرض
                </label>

                <div className='row g-2'>
                  {[
                    { id: 'showName', label: 'اسم الصنف', checked: vm.showName, setter: vm.setShowName, icon: 'bi-fonts' },
                    { id: 'showCode', label: 'الكود', checked: vm.showCode, setter: vm.setShowCode, icon: 'bi-tag' },
                    { id: 'showUom', label: 'الوحدة', checked: vm.showUom, setter: vm.setShowUom, icon: 'bi-rulers' },
                    { id: 'showPrice', label: 'السعر', checked: vm.showPrice, setter: vm.setShowPrice, icon: 'bi-cash' },
                    { id: 'showBarcodeText', label: 'نص الباركود', checked: vm.showBarcodeText, setter: vm.setShowBarcodeText, icon: 'bi-upc' }
                  ].map(opt => (
                    <div key={opt.id} className='col-6'>
                      <Card>
                        <div
                          className={`hover-pointer ${opt.checked ? 'border-theme bg-theme bg-opacity-5' : ''}`}
                          onClick={() => opt.setter(!opt.checked)}
                        >
                          <div className='card-body p-2'>
                            <div className='d-flex align-items-center'>
                              <div className='form-check mb-0'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id={opt.id}
                                  checked={opt.checked}
                                  onChange={e => opt.setter(e.target.checked)}
                                />
                              </div>
                              <label className='form-check-label small ms-2 mb-0' htmlFor={opt.id}>
                                <i className={`bi ${opt.icon} me-1`} />
                                {opt.label}
                              </label>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {/* CUSTOM */}
          {settingsTab === 'custom' ? (
            <div className='row g-3'>
              {/* Preview */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-eye me-1'></i>
                  معاينة الملصق
                </label>

                <div className='border rounded p-2 bg-whitee' style={{ width: '100%', overflow: 'auto' }}>
                  <div
                    style={{
                      width: previewWpx,
                      height: previewHpx,
                      position: 'relative',
                      border: '1px dashed rgba(0,0,0,0.25)',
                      background: 'rgba(255,255,255,0.8)'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage:
                          'linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)',
                        backgroundSize: `${10 * PX_PER_MM * previewScale}px ${10 * PX_PER_MM * previewScale}px`,
                        pointerEvents: 'none'
                      }}
                    />

                    {previewRow ? (
                      (() => {
                        const item = previewRow.item;
                        const name = vm.getDisplayName(item) || 'اسم المنتج';
                        const uom = vm.getDisplayUom(item) || 'الوحدة';
                        const code = String(item?.itemCode ?? 'CODE');
                        const priceText = `${previewRow.price.toFixed(2)} ${vm.currency}`.trim();
                        const b = previewRow.barcode || '0123456789012';
                        const f = vm.layout.fields;

                        return (
                          <>
                            {!f.name.hidden && vm.showName && <div style={fieldStylePX(f.name, previewScale)}>{name}</div>}
                            {!f.price.hidden && vm.showPrice && <div style={fieldStylePX(f.price, previewScale)}>{priceText}</div>}
                            {!f.uom.hidden && vm.showUom && <div style={fieldStylePX(f.uom, previewScale)}>{uom}</div>}
                            {!f.code.hidden && vm.showCode && <div style={fieldStylePX(f.code, previewScale)}>{code}</div>}

                            {!f.barcode.hidden && (
                              <div
                                style={{
                                  ...fieldStylePX(f.barcode, previewScale),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Barcode
                                  value={b}
                                  format={f.barcode.format ?? 'CODE128'}
                                  displayValue={f.barcode.showValue ?? false}
                                  margin={0}
                                  renderer='svg'
                                  width={f.barcode.barWidthPx ?? 1.3}
                                  height={Math.max(20, Math.floor(f.barcode.hMm * PX_PER_MM * previewScale))}
                                />
                              </div>
                            )}

                            {!f.barcodeText.hidden && vm.showBarcodeText && (
                              <div style={fieldStylePX(f.barcodeText, previewScale)}>{b}</div>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <div className='text-muted small p-2'>اختر صنف أو حدِّد أصناف لرؤية المعاينة.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Label size + Save/Load */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-arrows-fullscreen me-1'></i>
                  أبعاد الملصق (mm)
                </label>

                <div className='row g-2'>
                  <div className='col-4'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text'>W</span>
                      <input
                        type='number'
                        step='0.1'
                        className='form-control'
                        value={vm.layout.labelWmm}
                        onChange={e => vm.setLayout(prev => ({ ...prev, labelWmm: Number(e.target.value || 0) }))}
                      />
                    </div>
                  </div>

                  <div className='col-4'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text'>H</span>
                      <input
                        type='number'
                        step='0.1'
                        className='form-control'
                        value={vm.layout.labelHmm}
                        onChange={e => vm.setLayout(prev => ({ ...prev, labelHmm: Number(e.target.value || 0) }))}
                      />
                    </div>
                  </div>

                  <div className='col-4'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text'>Gap</span>
                      <input
                        type='number'
                        step='0.1'
                        className='form-control'
                        value={vm.layout.gapMm}
                        onChange={e => vm.setLayout(prev => ({ ...prev, gapMm: Number(e.target.value || 0) }))}
                      />
                    </div>
                  </div>
                </div>

                <div className='d-flex gap-2 mt-2 flex-wrap'>
                  <button className='btn btn-outline-secondary btn-sm' type='button' onClick={saveLayout}>
                    <i className='bi bi-save me-1'></i> حفظ
                  </button>
                  <button className='btn btn-outline-secondary btn-sm' type='button' onClick={loadLayout}>
                    <i className='bi bi-download me-1'></i> تحميل
                  </button>

                  <button className='btn btn-outline-danger btn-sm' type='button' onClick={() => vm.setLayout(presetRoll80x40())}>
                    <i className='bi bi-arrow-counterclockwise me-1'></i> Reset
                  </button>
                </div>
              </div>

              {/* Field select + editor */}
              <div className='col-12'>
                <label className='form-label small fw-semibold mb-2'>
                  <i className='bi bi-bounding-box me-1'></i>
                  مواقع العناصر (X,Y,W,H)
                </label>

                <div className='row g-2 align-items-end mb-2'>
                  <div className='col-12'>
                    <div className='input-group input-group-sm'>
                      <span className='input-group-text'>
                        <i className='bi bi-ui-radios-grid me-1'></i>
                        العنصر
                      </span>
                      <select
                        className='form-select'
                        value={vm.selectedFieldKey}
                        onChange={e => vm.setSelectedFieldKey(e.target.value as FieldKey)}
                      >
                        {FIELD_OPTIONS.map(opt => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='text-muted small mt-1'>اختر عنصر واحد لضبط إحداثياته .</div>
                  </div>
                </div>

                <FieldEditorCard
                  title={vm.selectedField.label}
                  fieldKey={vm.selectedField.key}
                  isBarcode={vm.selectedField.isBarcode}
                  layout={vm.layout}
                  setLayoutField={vm.setLayoutField}
                />
              </div>
            </div>
          ) : null}

          {/* Print button */}
          <div className='mt-3'>
            <button
              className='btn btn-theme btn-lg w-100 d-flex align-items-center justify-content-center'
              type='button'
              onClick={onPrint}
              disabled={vm.labels.length === 0 || !vm.selectedPriceListId}
            >
              <i className='bi bi-printer-fill me-2'></i>
              {!vm.selectedPriceListId
                ? 'اختر قائمة سعر أولاً'
                : vm.labels.length === 0
                ? 'لا يوجد ملصقات للطباعة'
                : `طباعة ${vm.labels.length} ملصق`}
            </button>

            {vm.labels.length > 0 && vm.selectedPriceListId && (
              <div className='text-center mt-2 small text-muted'>
                <i className='bi bi-info-circle me-1'></i>
                سيتم فتح نافذة جديدة للطباعة
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
